import { importLibrary, setOptions } from '@googlemaps/js-api-loader'

/**
 * Google Maps, cargado una sola vez.
 *
 * MEGA escogió Google sobre OpenStreetMap el 15 de agosto de 2026, y por una
 * razón medida: OSM no tiene ninguna de las direcciones con nombre de sitio
 * —«Chalet Los Naranjos», «Sendero Campestre casa 9», «Manzana I casa 5»—,
 * que es como se escriben la mayoría de las direcciones en Cartago. Se
 * probaron las cinco que fallaban, también en texto libre: ninguna existe en
 * OSM. Google sí las tiene.
 *
 * Va todo con Google —mapa y búsqueda— y no mezclado, porque las condiciones
 * de Google Maps Platform no permiten mostrar resultados geocodificados por
 * Google sobre un mapa que no sea de Google.
 *
 * LA LLAVE ES PÚBLICA. Cualquiera que abra la aplicación la ve: así funcionan
 * las llaves de navegador. Lo que la protege no es esconderla sino
 * restringirla en Google Cloud a los dominios de MEGA y a las APIs que se
 * usan. Sin esa restricción, cualquiera puede gastar con ella.
 */

const LLAVE = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined

export function hayLlaveDeGoogle(): boolean {
  return Boolean(LLAVE && LLAVE.trim())
}

/** Lo que la aplicación necesita de Google, ya cargado. */
export type ApiDeGoogle = {
  maps: google.maps.MapsLibrary
  geocoding: google.maps.GeocodingLibrary
  geometry: google.maps.GeometryLibrary
}

let cargador: Promise<ApiDeGoogle> | null = null

/**
 * `setOptions` se llama UNA sola vez, aunque se pidan el mapa completo y la
 * geocodificación por separado: repetirlo con otra configuración confunde al
 * cargador de Google.
 */
let configurado = false
function configurar(): void {
  if (configurado) return
  setOptions({ key: LLAVE!, v: 'weekly', language: 'es', region: 'CO' })
  configurado = true
}

/* ── Cuando Google rechaza la llave ─────────────────────────────────────────
   NO falla la promesa: el script carga bien y después Google pinta su propio
   cartel gris DENTRO del div del mapa —«Se ha producido un error… consulte la
   consola»—. Un `.catch` nunca se entera, así que la pantalla del técnico
   acababa con medio alto ocupado por un mensaje de Google que no le dice nada
   y que él no puede arreglar.

   `gm_authFailure` es el gancho que Google deja justo para esto. Se levanta una
   bandera y cada mapa enseña su propio vacío, en nuestras palabras. */
let llaveRechazada = false
const avisados = new Set<() => void>()

export function llaveDeGoogleRechazada(): boolean {
  return llaveRechazada
}

/** Avisa cuando Google rechace la llave. Devuelve cómo dejar de escuchar. */
export function alFallarLaLlave(fn: () => void): () => void {
  avisados.add(fn)
  return () => avisados.delete(fn)
}

declare global {
  interface Window {
    gm_authFailure?: () => void
  }
}

if (typeof window !== 'undefined') {
  window.gm_authFailure = () => {
    llaveRechazada = true
    avisados.forEach((fn) => fn())
  }
}

/**
 * Devuelve la API, cargándola la primera vez.
 *
 * Se guarda la promesa y no el resultado para que dos pantallas que la pidan
 * a la vez no disparen dos cargas del script.
 */
export function cargarGoogleMaps(): Promise<ApiDeGoogle> {
  if (!hayLlaveDeGoogle()) {
    return Promise.reject(
      new Error('Falta la llave de Google Maps. Se configura en VITE_GOOGLE_MAPS_API_KEY.'),
    )
  }

  if (!cargador) {
    configurar()

    // geometry da el cálculo de distancias con el que se escalan los
    // círculos; geocoding, la búsqueda de direcciones.
    cargador = Promise.all([
      importLibrary('maps'),
      importLibrary('geocoding'),
      importLibrary('geometry'),
    ])
      .then(([maps, geocoding, geometry]) => ({ maps, geocoding, geometry }))
      .catch((fallo: unknown) => {
        // Se suelta la promesa fallida: si el primer intento se cayó por la
        // red, el siguiente debe poder volver a intentarlo.
        cargador = null
        throw fallo
      })
  }

  return cargador
}

let cargadorGeocoder: Promise<google.maps.GeocodingLibrary> | null = null

/**
 * SOLO LA GEOCODIFICACIÓN, sin el peso del mapa ni de geometry.
 *
 * Para estampar la foto del técnico no hace falta pintar ningún mapa: solo
 * convertir el punto del GPS en una dirección. En el APK, sobre datos móviles y
 * a la primera foto, cargar además `maps` y `geometry` era lo que hacía que la
 * dirección no llegara a tiempo y la foto saliera con las coordenadas en grados.
 * Esta carga es una fracción de aquella.
 *
 * Comparte la configuración de la llave con `cargarGoogleMaps`, así que da igual
 * cuál se pida primero.
 */
export function cargarGeocodificacion(): Promise<google.maps.GeocodingLibrary> {
  if (!hayLlaveDeGoogle()) {
    return Promise.reject(
      new Error('Falta la llave de Google Maps. Se configura en VITE_GOOGLE_MAPS_API_KEY.'),
    )
  }

  if (!cargadorGeocoder) {
    configurar()
    cargadorGeocoder = importLibrary('geocoding').catch((fallo: unknown) => {
      cargadorGeocoder = null
      throw fallo
    })
  }

  return cargadorGeocoder
}


/**
 * EL MAPA SIN LA PUBLICIDAD DE GOOGLE.
 *
 * Google pinta por defecto sus puntos de interés —moteles, restaurantes,
 * supermercados, gimnasios— con icono de color y nombre. En un mapa de turismo
 * eso es el producto; en el nuestro es RUIDO: compite con los puntos de las
 * órdenes, que son del mismo tamaño y también de colores, y obliga a buscar el
 * trabajo entre negocios que no tienen nada que ver con MEGA.
 *
 * Lo que SÍ se queda son las calles con sus nombres y los barrios: así es como
 * se ubica una dirección en Cartago, y sin ellos el mapa no sirve para nada.
 *
 * Vive aquí y no en cada pantalla para que los cuatro mapas de la aplicación
 * —órdenes, calor por zona, trabajo del día y sitio de la orden— se vean igual
 * y se cambien de una sola vez.
 */
export const ESTILO_MAPA: google.maps.MapTypeStyle[] = [
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
]
